/**
 * Shared cryptographic utility functions for binary-to-text conversions.
 * Used by `useEncryption` and `useSeed`.
 */

/**
 * Converts an ArrayBuffer or ArrayBufferView to a base64-encoded string.
 * @param buffer - The buffer to encode.
 */
export function arrayBufferToBase64(buffer: ArrayBufferLike | ArrayBufferView): string {
  const bytes = ArrayBuffer.isView(buffer)
    ? new Uint8Array(buffer.buffer as ArrayBuffer, buffer.byteOffset, buffer.byteLength)
    : new Uint8Array(buffer as ArrayBuffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0)
  }
  return btoa(binary)
}

/**
 * Converts a base64-encoded string back to an ArrayBuffer.
 * @param base64 - The base64 string to decode.
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Converts an ArrayBuffer to a hexadecimal string.
 * @param buffer - The ArrayBuffer to encode.
 */
export function arrayBufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Converts a Uint8Array to a binary string of 0s and 1s.
 * @param bytes - The byte array to convert.
 */
export function bytesToBits(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(2).padStart(8, '0'))
    .join('')
}

/**
 * Wraps an AES-GCM CryptoKey using AES-KW with the given wrapper key.
 * @param keyToWrap - The AES-GCM key to wrap (must be extractable).
 * @param wrapperKey - The AES-KW wrapping key.
 * @returns Base64-encoded wrapped key bytes.
 */
export async function wrapKey(
  keyToWrap: CryptoKey,
  wrapperKey: CryptoKey
): Promise<string> {
  const wrapped = await crypto.subtle.wrapKey('raw', keyToWrap, wrapperKey, 'AES-KW')
  return arrayBufferToBase64(wrapped)
}

/**
 * Unwraps a previously wrapped AES-GCM key using AES-KW.
 * @param wrappedKeyBase64 - Base64-encoded wrapped key bytes.
 * @param wrapperKey - The AES-KW unwrapping key.
 * @returns The unwrapped AES-GCM CryptoKey.
 */
export async function unwrapKey(
  wrappedKeyBase64: string,
  wrapperKey: CryptoKey
): Promise<CryptoKey> {
  const wrappedBytes = base64ToArrayBuffer(wrappedKeyBase64)
  return crypto.subtle.unwrapKey(
    'raw',
    wrappedBytes,
    wrapperKey,
    'AES-KW',
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )
}

/** Default PBKDF2 iteration count for passphrase-based encryption. */
const PBKDF2_ITERATIONS = 600_000

/**
 * Encrypts a plaintext string using a passphrase via PBKDF2 + AES-GCM.
 * Returns a payload in the format `saltBase64.ivBase64.ciphertextBase64`.
 * @param plaintext - The string to encrypt.
 * @param passphrase - The user-provided passphrase.
 * @param iterations - PBKDF2 iterations (default: 600,000).
 */
export async function encryptWithPassphrase(
  plaintext: string,
  passphrase: string,
  iterations: number = PBKDF2_ITERATIONS
): Promise<string> {
  const enc = new TextEncoder()
  const salt = crypto.getRandomValues(new Uint8Array(16))
  const iv = crypto.getRandomValues(new Uint8Array(12))

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  )

  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  )

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    enc.encode(plaintext)
  )

  return `${arrayBufferToBase64(salt)}.${arrayBufferToBase64(iv)}.${arrayBufferToBase64(ciphertext)}`
}

/**
 * Decrypts a payload encrypted with {@link encryptWithPassphrase}.
 * @param payload - String in `saltBase64.ivBase64.ciphertextBase64` format.
 * @param passphrase - The passphrase used during encryption.
 * @param iterations - PBKDF2 iterations (must match encryption, default: 600,000).
 */
export async function decryptWithPassphrase(
  payload: string,
  passphrase: string,
  iterations: number = PBKDF2_ITERATIONS
): Promise<string> {
  const parts = payload.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid passphrase-encrypted payload format')
  }

  const [saltB64, ivB64, ciphertextB64] = parts as [string, string, string]
  const salt = new Uint8Array(base64ToArrayBuffer(saltB64))
  const iv = new Uint8Array(base64ToArrayBuffer(ivB64))
  const ciphertext = base64ToArrayBuffer(ciphertextB64)

  const enc = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveKey']
  )

  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  )

  const plainBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )

  return new TextDecoder().decode(plainBuffer)
}

/** Fixed salt for recovery ID derivation — must never change. */
const RECOVERY_ID_SALT = 'minimind-recovery-id'

/**
 * Derives a deterministic recovery identifier from a passphrase.
 * Uses PBKDF2 with a fixed salt to produce a hex string that serves
 * as a database lookup key. Without the passphrase, the ID is not guessable.
 * @param passphrase - The recovery passphrase.
 * @param iterations - PBKDF2 iterations (default: 600,000). Pass 100,000 only for legacy migration lookups.
 * @returns 64-character hex string.
 */
export async function deriveRecoveryId(
  passphrase: string,
  iterations: number = PBKDF2_ITERATIONS
): Promise<string> {
  const enc = new TextEncoder()

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveBits']
  )

  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: enc.encode(RECOVERY_ID_SALT),
      iterations,
      hash: 'SHA-256'
    },
    keyMaterial,
    256
  )

  return arrayBufferToHex(bits)
}

/**
 * Encrypts a JSON-serializable object with an AES-GCM key.
 * Returns a payload in the format `ivBase64.ciphertextBase64`.
 * @param data - The object to encrypt.
 * @param key - The AES-GCM key to encrypt with.
 */
export async function encryptWithKey(
  data: Record<string, unknown>,
  key: CryptoKey
): Promise<string> {
  const plaintext = new TextEncoder().encode(JSON.stringify(data))
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext)
  return `${arrayBufferToBase64(iv)}.${arrayBufferToBase64(ciphertext)}`
}

/**
 * Decrypts a `ivBase64.ciphertextBase64` payload with an AES-GCM key.
 * @param encrypted - The encrypted payload string.
 * @param key - The AES-GCM key to decrypt with.
 */
export async function decryptWithKey(
  encrypted: string,
  key: CryptoKey
): Promise<Record<string, unknown>> {
  const dotIndex = encrypted.indexOf('.')
  if (dotIndex === -1) throw new Error('Invalid encrypted data format')
  const iv = base64ToArrayBuffer(encrypted.substring(0, dotIndex))
  const ciphertext = base64ToArrayBuffer(encrypted.substring(dotIndex + 1))
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    key,
    ciphertext
  )
  return JSON.parse(new TextDecoder().decode(decrypted))
}
